/**
 * @file StockLevelsFilters.tsx
 * @description Фильтры вкладки «Остатки» (phase19.24 этап 1, шаг 2;
 * два ряда — phase19.31, SC59).
 *
 * Раскладка SC59: первый ряд — поиск, склад и «Сбросить»; второй — пилюли
 * отбора. Отдельного поля «Партия» нет: поиск ищет по названию, артикулу и
 * номеру партии (старый параметр адреса `lot=` подставляется в поиск —
 * `useStockFilters`).
 *
 * Все переключатели уходят на сервер (`onlyPositive` / `onlySellable` /
 * `unplaced` / `labels` / `belowMin`), на клиенте доводится только поиск.
 * Пилюли — `FilterToggleChips` (SC45); на телефоне они в шторке
 * «Фильтры · N» (`chipsCollapse`), чтобы на 360 под фильтрами помещался
 * список.
 */

import { RotateCcw } from "lucide-react";

import type { StockFilters } from "./use-stock-filters";
import { Button } from "../../ui/button";
import { FilterBar } from "../../ui/FilterBar";
import { FilterToggleChips, type FilterToggle } from "../../ui/FilterChips";
import { SearchInput } from "../../ui/SearchInput";
import { SelectField } from "../../ui/SelectField";
import type { WarehouseDto } from "../../../lib/api-wms";
import { PHONE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";

interface Props {
  filters: StockFilters;
  onChange: (patch: Partial<StockFilters>) => void;
  onReset: () => void;
  dirty: boolean;
  warehouses: WarehouseDto[];
}

/** Пилюли отбора — данными: новая пилюля = запись (OCP). */
function stockToggles(filters: StockFilters, onChange: Props["onChange"]): FilterToggle[] {
  // Отборы независимы: каждый включается сам по себе. «Без места» и
  // «Ниже минимума» приходят включёнными по ссылке с обзора склада —
  // человек видит, чем отобран список.
  return [
    { key: "positive", label: "Только с остатком", pressed: filters.onlyPositive, onToggle: () => onChange({ onlyPositive: !filters.onlyPositive }) },
    { key: "sellable", label: "Только продаваемые", pressed: filters.onlySellable, onToggle: () => onChange({ onlySellable: !filters.onlySellable }) },
    { key: "unplaced", label: "Без места", pressed: filters.unplaced, onToggle: () => onChange({ unplaced: !filters.unplaced }) },
    { key: "no-labels", label: "Без этикеток", pressed: filters.labels === "none", onToggle: () => onChange({ labels: filters.labels === "none" ? "" : "none" }) },
    { key: "stale-labels", label: "Этикетки устарели", pressed: filters.labels === "stale", onToggle: () => onChange({ labels: filters.labels === "stale" ? "" : "stale" }) },
    { key: "below-min", label: "Ниже минимума", pressed: filters.belowMin, onToggle: () => onChange({ belowMin: !filters.belowMin }) },
  ];
}

export function StockLevelsFilters({ filters, onChange, onReset, dirty, warehouses }: Props) {
  const toggles = stockToggles(filters, onChange);
  const phone = useMediaQuery(PHONE_QUERY);

  return (
    <FilterBar
      chipsBelow
      chipsCollapse={{
        activeCount: toggles.filter((t) => t.pressed).length,
        // «Сбросить» в шторке возвращает пилюли к значениям по умолчанию.
        onReset: () => onChange({ onlyPositive: true, onlySellable: false, unplaced: false, labels: "", belowMin: false }),
      }}
      search={
        <SearchInput
          value={filters.search}
          onChange={(search) => onChange({ search })}
          // На 360 длинная подсказка обрезалась — на телефоне короче.
          placeholder={phone ? "Название, артикул, партия" : "Название, артикул или номер партии"}
          ariaLabel="Поиск по остаткам: название, артикул или номер партии"
        />
      }
      chips={<FilterToggleChips aria-label="Отбор остатков" items={toggles} />}
    >
      <SelectField
        value={filters.warehouseId}
        onChange={(warehouseId) => onChange({ warehouseId })}
        options={[
          { value: "", label: "Все склады" },
          ...warehouses.map((w) => ({ value: w.id, label: w.name })),
        ]}
        placeholder="Все склады"
        ariaLabel="Склад"
        // На телефоне склад делит ряд с кнопкой «Фильтры» и «Сбросить».
        className="min-w-0 flex-1 sm:w-[220px] sm:flex-none"
      />
      {dirty ? (
        <Button type="button" variant="outline" onClick={onReset} aria-label="Сбросить фильтры" className="min-w-[var(--control-h,3rem)] shrink-0">
          <RotateCcw className="size-4" aria-hidden />
          <span className="hidden sm:inline">Сбросить</span>
        </Button>
      ) : null}
    </FilterBar>
  );
}
